import { useEffect, useRef, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  Box,
  Button,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { goBack, submitAnswer } from '../../core/api/client'
import { translateQuestion } from '../../core/diagnostic'
import LoadingScreen from '../LoadingScreen'
import OptionCard from './OptionCard'
import SectorCollectionQuestion from './SectorCollectionQuestion'
import StageRail from './StageRail'

function nextMultiSelection(questionId, current, value) {
    const values = Array.isArray(current) ? current : []
    return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

export default function Questionnaire({ sessionToken, question, setQuestion, setError, onReturnHome }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [selected, setSelected] = useState(question?.value ?? (question?.type === 'multi' ? [] : null))
    const [submitting, setSubmitting] = useState(false)
    const submitValueRef = useRef(null)
    const translatedQuestion = translateQuestion(question, t)

    useEffect(() => {
        setSelected(question?.value ?? (question?.type === 'multi' ? [] : null))
    }, [question])

    async function submitValue(value) {
        if (!question || submitting) {
            return
        }

        setSubmitting(true)
        setError('')

        try {
            const response = await submitAnswer(sessionToken, question.id, value)
            if (response.nextQuestion) {
                setQuestion(response.nextQuestion)
            } else {
                navigate(`/report/${sessionToken}`)
            }
        } catch (caught) {
            setError(caught.message)
        } finally {
            setSubmitting(false)
        }
    }

    submitValueRef.current = submitValue

    async function handleBack() {
        if (!question?.canGoBack || submitting) {
            return
        }

        setSubmitting(true)
        setError('')

        try {
            const response = await goBack(sessionToken)
            setQuestion(response.nextQuestion)
        } catch (caught) {
            setError(caught.message)
        } finally {
            setSubmitting(false)
        }
    }

    async function handleStageSelect(targetStage) {
        if (!question?.canGoBack || submitting || targetStage >= question.stage) {
            return
        }

        setSubmitting(true)
        setError('')

        try {
            let nextQuestion = question
            while (nextQuestion?.canGoBack && nextQuestion.stage > targetStage) {
                const response = await goBack(sessionToken)
                nextQuestion = response.nextQuestion
            }

            if (nextQuestion) {
                setQuestion(nextQuestion)
            }
        } catch (caught) {
            setError(caught.message)
        } finally {
            setSubmitting(false)
        }
    }

    function toggleMulti(value) {
        setSelected((current) => nextMultiSelection(question?.id, current, value))
    }

    useEffect(() => {
        function onKeyDown(event) {
            if (!question || submitting) {
                return
            }

            const number = Number.parseInt(event.key, 10)
            if (number >= 1 && number <= question.options.length) {
                const option = question.options[number - 1]
                if (question.type === 'multi') {
                    setSelected((current) => nextMultiSelection(question.id, current, option.value))
                } else {
                    submitValueRef.current?.(option.value)
                }
            }

            if (event.key === 'Enter' && question.type === 'multi') {
                submitValueRef.current?.(Array.isArray(selected) ? selected : [])
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [question, selected, submitting])

    if (!translatedQuestion) {
        return <LoadingScreen />
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Grid container spacing={2.5} minHeight={{ md: 'calc(100vh - 88px)' }}>
                <Grid size={{ xs: 12, md: 3 }}>
                    <StageRail question={question} onStageSelect={handleStageSelect} disabled={submitting} />
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                    <Paper variant="outlined" sx={{ minHeight: '100%', p: { xs: 2.5, sm: 3, md: 4 }, bgcolor: 'background.paper' }}>
                        <Stack spacing={3} height="100%">
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                <Typography color="text.secondary">{translatedQuestion.stageLabel}</Typography>
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    variant="text"
                                    disabled={!translatedQuestion.canGoBack || submitting}
                                    onClick={handleBack}
                                >
                                    {t('questionnaire.back')}
                                </Button>
                            </Stack>
                            <Box>
                <Typography color="text.secondary">
                  {t('questionnaire.stepOf', { step: translatedQuestion.stage, total: translatedQuestion.totalStages })}
                </Typography>
                                <Typography variant="h3" mt={1} lineHeight={1.15} maxWidth={760}>
                                    {translatedQuestion.prompt}
                                </Typography>
                                {translatedQuestion.helperText ? (
                                    <Typography variant="body2" mt={2} color="text.secondary">
                                        {translatedQuestion.helperText}
                                    </Typography>
                                ) : null}
                            </Box>
                            {translatedQuestion.type === 'sectorCollection' ? (
                                <SectorCollectionQuestion
                                    question={translatedQuestion}
                                    disabled={submitting}
                                    onSubmit={submitValue}
                                    onReturnHome={onReturnHome}
                                />
                            ) : (
                                <Grid container spacing={1.5}>
                                    {translatedQuestion.options.map((option, index) => {
                                        const optionSelected =
                                            translatedQuestion.type === 'multi'
                                                ? Array.isArray(selected) && selected.includes(option.value)
                                                : selected === option.value
                                        const optionDisabled =
                                            submitting

                                        return (
                                            <Grid
                                                key={String(option.value)}
                                                size={{ xs: 12, md: translatedQuestion.options.length > 6 ? 6 : 12 }}
                                                sx={{ display: 'flex' }}
                                            >
                                                <OptionCard
                                                    option={option}
                                                    index={index}
                                                    selected={optionSelected}
                                                    disabled={optionDisabled}
                                                    multi={translatedQuestion.type === 'multi'}
                                                    onClick={() => {
                                                        if (translatedQuestion.type === 'multi') {
                                                            toggleMulti(option.value)
                                                        } else {
                                                            setSelected(option.value)
                                                            submitValue(option.value)
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            )}
                            {translatedQuestion.notes?.length && !translatedQuestion.id.startsWith('triage') ? (
                                <Stack spacing={0.75} mt={2} maxWidth={760}>
                                    {translatedQuestion.notes.map((note) => (
                                        <Typography key={note} variant="body2" color="text.secondary">
                                            {note}
                                        </Typography>
                                    ))}
                                </Stack>
                            ) : null}
                            {translatedQuestion.type === 'sectorCollection' ? null : translatedQuestion.type === 'multi' ? (
                                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5}>
                                    <Button
                                        size="large"
                                        color="primary"
                                        endIcon={submitting ? <CircularProgress color="inherit" size={16} /> : <ArrowForwardIcon />}
                                        disabled={submitting}
                                        onClick={() => submitValue(Array.isArray(selected) ? selected : [])}
                                    >
                                        {t('questionnaire.continue')}
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('questionnaire.multiHelp')}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t('questionnaire.singleHelp')}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    )
}
